import type React from 'react';
import { useMemo, useState } from 'react';

import classes from '../Chat.module.scss';
import screen from '../ChatScreen.module.scss';
import { formatDisplayName } from '../utils/formatUserName';
import type { ChatRole, ChatRoom } from '../types';

import type { StudentBulletinTutorResponse } from '../api/routes';
import { BulletinThreadList, type BulletinThreadItem } from './BulletinThreadList';
import { ChatList } from './ChatList';
import { ChatStatusTabs } from './ChatStatusTabs';
import { StudentTutorList } from './StudentTutorList';
import type { ChatRoomStatus } from '../api/routes';

interface ChatSidebarProps {
  role: ChatRole;
  chatStatus: ChatRoomStatus;
  onChatStatusChange: (status: ChatRoomStatus) => void;
  bulletinThreads: BulletinThreadItem[];
  bulletinsLoading: boolean;
  selectedBulletinId: number | null;
  onBulletinSelect: (bulletinId: number) => void;
  onBulletinBack?: () => void;
  bulletinBackLabel?: string;
  studentPanel?: 'bulletins' | 'tutors';
  bulletinTutors?: StudentBulletinTutorResponse[];
  bulletinTutorsLoading?: boolean;
  chatRooms: ChatRoom[];
  filteredChatRooms: ChatRoom[];
  /** Signed-in profile (only identity is needed here). */
  currentUser: { id: number } | null;
  currentUserId: number;
  chatRoomsLoading: boolean;
  selectedChatId?: string;
  onChatSelect: (chatId: string, roomId: string, userId: number) => void;
  onTutorSelect?: (tutor: StudentBulletinTutorResponse) => void;
  tutorResponseStatus?: 'all' | 'pending' | 'accepted' | 'rejected';
  onTutorResponseStatusChange?: (status: 'all' | 'pending' | 'accepted' | 'rejected') => void;
}

export const ChatSidebar: React.FC<ChatSidebarProps> = ({
  role,
  chatStatus,
  onChatStatusChange,
  bulletinThreads,
  bulletinsLoading,
  selectedBulletinId,
  onBulletinSelect,
  onBulletinBack,
  bulletinBackLabel = 'Мои заявки',
  studentPanel = 'bulletins',
  bulletinTutors = [],
  bulletinTutorsLoading = false,
  chatRooms,
  currentUser,
  currentUserId,
  chatRoomsLoading,
  selectedChatId,
  onChatSelect,
  onTutorSelect,
  tutorResponseStatus = 'all',
  onTutorResponseStatusChange,
}) => {
  const sidebarClass =
    role === 'tutor'
      ? classes['chat-list-sidebar--tutor']
      : classes['chat-list-sidebar--student'];

  const [nameQuery, setNameQuery] = useState('');
  const query = nameQuery.trim().toLowerCase();
  const roomsForList = useMemo(() => {
    if (!query) return chatRooms;
    return chatRooms.filter((room) =>
      formatDisplayName(room.other_user || {}, room.other_user_id).toLowerCase().includes(query),
    );
  }, [chatRooms, query]);
  const tutorsForList = useMemo(() => {
    if (!query) return bulletinTutors;
    return bulletinTutors.filter((tutor) =>
      formatDisplayName(
        {
          name: tutor.other_user.name,
          surname: tutor.other_user.surname,
          patronymic: tutor.other_user.patronymic,
        },
        tutor.tutor_id,
      )
        .toLowerCase()
        .includes(query),
    );
  }, [bulletinTutors, query]);

  const searchField = (
    <input
      className={screen.search}
      type='search'
      value={nameQuery}
      placeholder='Поиск по имени...'
      aria-label='Поиск по имени'
      onChange={(event) => setNameQuery(event.target.value)}
    />
  );

  if (role === 'tutor') {
    return (
      <div className={`${classes['chat-list-sidebar']} ${sidebarClass} ${screen.list}`}>
        <div className={classes['chat-sidebar-header']}>
          <h2 className={screen.listTitle}>Диалоги</h2>
          {searchField}
          <ChatStatusTabs value={chatStatus} onChange={onChatStatusChange} />
          <div className={classes['chat-response-filter']}>
            {(
              [
                ['all', 'Все отклики'],
                ['pending', 'Ожидают'],
                ['accepted', 'Приняты'],
                ['rejected', 'Отклонены'],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type='button'
                className={
                  tutorResponseStatus === value
                    ? classes['chat-response-filter--active']
                    : classes['chat-response-filter-btn']
                }
                onClick={() => onTutorResponseStatusChange?.(value)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <ChatList
          chatRooms={roomsForList}
          currentUser={currentUser}
          currentUserId={currentUserId}
          isLoading={chatRoomsLoading}
          role={role}
          selectedChatId={selectedChatId}
          onChatSelect={onChatSelect}
          embedded
          showTitle={false}
        />
      </div>
    );
  }

  return (
    <div className={`${classes['chat-list-sidebar']} ${sidebarClass} ${screen.list}`}>
      <div className={classes['chat-sidebar-header']}>
        <h2 className={screen.listTitle}>Диалоги</h2>
        {studentPanel === 'tutors' ? searchField : null}
        <ChatStatusTabs value={chatStatus} onChange={onChatStatusChange} />
      </div>

      {studentPanel === 'bulletins' ? (
        <BulletinThreadList
          threads={bulletinThreads}
          role={role}
          selectedBulletinId={selectedBulletinId}
          isLoading={bulletinsLoading}
          onSelect={onBulletinSelect}
          countMode='tutors'
          embedded
        />
      ) : (
        <>
          {onBulletinBack ? (
            <button
              type='button'
              className={classes['chat-sidebar-back']}
              onClick={onBulletinBack}
            >
              {bulletinBackLabel}
            </button>
          ) : null}
          <StudentTutorList
            tutors={tutorsForList}
            isLoading={bulletinTutorsLoading}
            currentUserId={currentUserId}
            selectedChatId={selectedChatId}
            onTutorSelect={onTutorSelect ?? (() => undefined)}
          />
        </>
      )}
    </div>
  );
};
